import { useState } from 'react'
import { Box, Divider, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { LaptopIcon, SmartphoneIcon, GlobeIcon, XIcon } from 'lucide-react'
import { Button, Skeleton } from '@repo/ui'

import { useSnackbar } from 'contexts'
import { getErrorMessage } from 'utils'
import { useUserSessions, useRevokeSession, useRevokeAllSessions } from 'hooks/api'
import { ConfirmSettingModal } from 'components/feedback/modals/ConfirmSettingModal'

/**
 * "Devices & sessions" on the personal Security page.
 *
 * Backed by the API's own session registry: one row per full sign-in, with the bounds the
 * auth guards enforce on every request. Active rows are the signed-in devices; the whole
 * list newest-first is the sign-in history. Signing a device out takes effect on that
 * device's next request.
 */

/**
 * Icon from the server's coarse device label ("Safari on iPhone", "Chrome on macOS"). The
 * label itself is what the row shows; the icon only needs a phone/computer split.
 */
const describeDevice = (deviceLabel: string | null) => {
  if (!deviceLabel) return { label: 'Unknown device', Icon: GlobeIcon }
  if (/iphone|ipad|android/i.test(deviceLabel)) return { label: deviceLabel, Icon: SmartphoneIcon }
  if (/macos|windows|linux|chromeos/i.test(deviceLabel)) return { label: deviceLabel, Icon: LaptopIcon }
  return { label: deviceLabel, Icon: GlobeIcon }
}

const ENDED_LABEL: Record<string, string> = {
  logout: 'Signed out',
  user_revoked: 'Signed out',
  signed_out_elsewhere: 'Signed out',
  password_changed: 'Ended by password change',
  factor_changed: 'Ended by 2FA change',
  staff_revoked: 'Ended by Vera staff',
  deactivated: 'Account deactivated',
  refresh_reuse: 'Ended for security',
}

const formatWhen = (iso: string) => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return 'Unknown'
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export const DevicesSection = () => {
  const theme = useTheme()
  const { open: notification } = useSnackbar()
  const { data, isLoading, refetch } = useUserSessions()
  const { mutateAsync: revokeSession, isPending: isRevoking } = useRevokeSession()
  const { mutateAsync: revokeAll, isPending: isRevokingAll } = useRevokeAllSessions()
  const [confirmAllOpen, setConfirmAllOpen] = useState(false)
  const [showAllHistory, setShowAllHistory] = useState(false)

  const sessions = data?.sessions ?? []
  // Devices = still-valid sessions. `revoked` is OUR column, not WorkOS's status:
  // the two disagree, and a device we have revoked must leave this list immediately
  // even if WorkOS still reports it active.
  const devices = sessions.filter(s => s.active)
  /*
    History is every session, including expired and revoked ones — that is precisely
    what makes it a sign-in log rather than a second copy of the device list.

    Collapsed to HISTORY_PREVIEW with an explicit "Show all" rather than a silent
    slice. A hard cap with no affordance is the failure working-conventions warns
    about: the list reads as complete, so someone checking whether an unfamiliar
    sign-in happened concludes it did not, when in fact it was simply past the cut.
    On a security surface that is the wrong way to be wrong.
  */
  const HISTORY_PREVIEW = 5
  const allHistory = sessions
  const history = showAllHistory ? allHistory : allHistory.slice(0, HISTORY_PREVIEW)
  const hiddenCount = allHistory.length - history.length

  const handleRevoke = async (sessionId: string) => {
    try {
      await revokeSession(sessionId)
      notification({ type: 'success', title: 'Device signed out' })
      refetch()
    } catch (err) {
      notification({ type: 'error', title: 'Could not sign out that device', text: getErrorMessage(err) })
    }
  }

  const handleRevokeAll = async () => {
    try {
      await revokeAll()
      setConfirmAllOpen(false)
      notification({
        type: 'success',
        title: 'Signed out everywhere else',
        text: 'Other devices will need to sign in again.',
      })
      refetch()
    } catch (err) {
      notification({ type: 'error', title: 'Could not sign out other devices', text: getErrorMessage(err) })
    }
  }

  // §19 — a skeleton that occupies the eventual layout, not a spinner.
  if (isLoading) {
    return (
      <Stack spacing={4}>
        <Skeleton lines={3} />
      </Stack>
    )
  }

  return (
    <Stack spacing={6}>
      <Stack spacing={2}>
        <Typography variant="body2" fontWeight={600}>
          Devices
        </Typography>

        {devices.length === 0 ? (
          <Typography variant="body3" color="text.secondary">
            No other active sessions.
          </Typography>
        ) : (
          /*
            Divider-separated ROWS, not bordered cards.
            
            The first version used cards, which put two different row idioms in one
            page: the Two-factor list directly above is `<Stack divider={<Divider/>}>`
            of 40px-circle + two-line + trailing IconButton rows, and this sat beneath
            it as outlined boxes. Same page, same kind of list, two visual languages.
            This now reuses that shape exactly (§28 — match the house pattern rather
            than inventing beside it).
          */
          <Stack divider={<Divider />} spacing={0}>
            {devices.map(session => {
              const { label, Icon } = describeDevice(session.deviceLabel)
              return (
                <Stack
                  key={session.id}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ py: 4 }}
                >
                  <Stack direction="row" alignItems="center" spacing={3}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: theme.palette.primary[50] || theme.palette.grey[100],
                        color: theme.palette.text.primary,
                        flexShrink: 0,
                      }}
                    >
                      <Icon size={18} strokeWidth={1.5} />
                    </Box>
                    <Stack spacing={0.5}>
                      <Stack direction="row" spacing={2} alignItems="baseline">
                        <Typography variant="body2">{label}</Typography>
                        {session.current && (
                          <Typography variant="body3" color="text.secondary">
                            This device
                          </Typography>
                        )}
                      </Stack>
                      <Typography variant="body3" color="text.secondary">
                        Signed in {formatWhen(session.createdAt)} · Last active {formatWhen(session.lastSeenAt)}
                      </Typography>
                    </Stack>
                  </Stack>

                  {/*
                    No control at all on the CURRENT session rather than a disabled one
                    (§46: record state hides, permission disables) — a button that signs
                    you out of the page you are standing on is a footgun, and "This
                    device" already explains the absence.

                    X, not the Trash the 2FA rows use, and the difference is deliberate
                    (§38): removing a factor DELETES its `user_auth_factors` row, while
                    revoking a session severs access to a device that still exists and
                    can sign in again. Same row shape, same IconButton treatment — only
                    the glyph differs, because only the consequence differs.
                  */}
                  {!session.current && (
                    <Tooltip title={`Sign out this ${label.toLowerCase()}`}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={isRevoking}
                          onClick={() => handleRevoke(session.id)}
                          aria-label={`Sign out ${label}`}
                          sx={{
                            color: theme.palette.text.secondary,
                            '&:hover': { color: theme.palette.error.main },
                          }}
                        >
                          <XIcon strokeWidth={1} size={18} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Stack>
              )
            })}
          </Stack>
        )}

        {/*
          `pt` ADDS to the Stack's own 8px, giving 24px between the list (or the empty-state
          line) and this button — `mt` would be overridden by the Stack, which owns the
          margin-top of every child after the first.

          The Stack's `spacing` cannot carry this on its own: it governs the heading→list gap
          too, and widening that to separate the action would push "Devices" away from the
          list it labels. An action that ends a section wants more air than the rows inside it,
          so the extra belongs on the action.
        */}
        <Box sx={{ pt: 4 }}>
          <Button
            variant="secondary"
            destructive="hover"
            disabled={isRevokingAll}
            onClick={() => setConfirmAllOpen(true)}
          >
            Sign out of all other devices
          </Button>
        </Box>
      </Stack>

      <Stack spacing={2}>
        <Typography variant="body2" fontWeight={600}>
          Recent sessions
        </Typography>
        {history.length === 0 ? (
          <Typography variant="body3" color="text.secondary">
            No sessions recorded yet.
          </Typography>
        ) : (
          /*
            A full-width divider-separated LIST, not the two-column text block this
            was. The first version packed date and detail into two narrow columns with
            no separation, so five rows read as a paragraph — which is the opposite of
            what a sign-in history is for (scanning for the one line that looks wrong).
            Same row rhythm as Devices above, one level quieter.
          */
          <Stack divider={<Divider />} spacing={0}>
            {history.map(session => {
              const { label, Icon } = describeDevice(session.deviceLabel)
              const isActive = session.active
              return (
                <Stack
                  key={`h-${session.id}`}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={3}
                  sx={{ py: 4 }}
                >
                  <Stack direction="row" alignItems="center" spacing={3} sx={{ minWidth: 0 }}>
                    <Icon
                      size={18}
                      strokeWidth={1.5}
                      color={theme.palette.text.secondary}
                      style={{ flexShrink: 0 }}
                    />
                    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                      <Typography variant="body2">
                        {formatWhen(session.createdAt)}
                      </Typography>
                      <Typography variant="body3" color="text.secondary">
                        {label}
                      </Typography>
                    </Stack>
                  </Stack>

                  {/* Only the non-current, non-active rows carry a status word. Marking
                      every row "Active" would put a label on the majority case and make
                      the exceptions harder to spot, not easier. */}
                  {!isActive && (
                    <Typography variant="body3" color="text.secondary" sx={{ flexShrink: 0 }}>
                      {session.revokedAt
                        ? ENDED_LABEL[session.revokedReason ?? ''] ?? 'Signed out'
                        : 'Expired'}
                    </Typography>
                  )}
                </Stack>
              )
            })}
          </Stack>
        )}

        {/* Counts what is hidden rather than a bare "Show more" — the number is the
            reason to click, and it also tells someone who does NOT click how much they
            are not seeing. */}
        {hiddenCount > 0 && !showAllHistory && (
          <Box>
            <Button variant="tertiary" onClick={() => setShowAllHistory(true)}>
              {`Show ${hiddenCount} more`}
            </Button>
          </Box>
        )}
        {showAllHistory && allHistory.length > HISTORY_PREVIEW && (
          <Box>
            <Button variant="tertiary" onClick={() => setShowAllHistory(false)}>
              Show less
            </Button>
          </Box>
        )}
      </Stack>

      {/*
        §43 — this one earns a gate. The effect is downstream and not recoverable by
        undoing it: every other device is signed out and has to authenticate again,
        and you cannot un-sign-out someone mid-task. The consequence line says what
        actually happens rather than asking "are you sure?".
      */}
      <ConfirmSettingModal
        open={confirmAllOpen}
        onClose={() => setConfirmAllOpen(false)}
        onConfirm={handleRevokeAll}
        title="Sign out of all other devices?"
        consequence="Every other phone, tablet and computer signed in to your account will be signed out on its next request and will need to sign in again, with your second factor. This device stays signed in."
        confirmLabel="Sign out other devices"
        destructive
        loading={isRevokingAll}
      />
    </Stack>
  )
}
